<template>
  <v-row>
    <v-col
      v-for="s in sections"
      :key="s.id"
      cols="12"
      :md="s.width || 12"
    >
      <!-- KPI tiles -->
      <v-row v-if="s.type === 'kpi'" dense>
        <v-col v-for="(it, i) in s.items" :key="i" cols="6" sm="4" md="auto" class="flex-grow-1">
          <v-card outlined>
            <v-card-text class="py-3">
              <div class="text-h6 font-weight-bold">{{ kpiValue(it) }}</div>
              <div class="text-caption grey--text text--darken-1">{{ it.label }}</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Table -->
      <v-card v-else-if="s.type === 'table'" outlined>
        <v-card-title class="text-subtitle-1 d-flex flex-wrap align-center">
          <span v-if="s.title">{{ s.title }}</span>
          <span class="ml-2 text-caption grey--text">
            {{ shownRows(s).length === rowsOf(s.dataset).length ? `${rowsOf(s.dataset).length} record(s)` : `${shownRows(s).length} of ${rowsOf(s.dataset).length} record(s)` }}
          </span>
          <v-spacer />
          <span v-if="hasLinks(s)" class="text-caption grey--text mr-3">
            <v-icon x-small>mdi-open-in-new</v-icon> Double-click or middle-click a record to open it in a new tab
          </span>
          <v-text-field
            v-if="s.searchable !== false" :value="search[s.id]" dense outlined hide-details clearable prepend-inner-icon="mdi-magnify"
            label="Search this table" style="max-width: 240px" class="rb-tsearch" @input="$set(search, s.id, $event)"
          />
          <v-menu v-if="s.downloadable !== false" offset-y>
            <template v-slot:activator="{ on }">
              <v-btn small outlined class="ml-2" title="Download this table's raw data (plain numbers, no currency symbols)" v-on="on">
                <v-icon left small>mdi-download</v-icon>Download
              </v-btn>
            </template>
            <v-list dense>
              <v-subheader>{{ shownRows(s).length }} row(s), plain values</v-subheader>
              <v-list-item @click="download(s, 'csv')"><v-list-item-title>CSV</v-list-item-title></v-list-item>
              <v-list-item @click="download(s, 'xlsx')"><v-list-item-title>Excel</v-list-item-title></v-list-item>
            </v-list>
          </v-menu>
        </v-card-title>
        <v-data-table
          dense
          mobile-breakpoint="0"
          :headers="headersFor(s)"
          :items="shownRows(s)"
          :sort-by="defaultSortBy(s)"
          :sort-desc="defaultSortDesc(s)"
          :items-per-page="s.pageSize || 25"
          :footer-props="{ 'items-per-page-options': [10, 25, 50, 100, -1] }"
        >
          <template v-slot:item="{ item }">
            <tr
              :class="{ 'rb-row-linked': rowLink(s, item) }"
              :title="rowLink(s, item) ? 'Double-click or middle-click to open in a new tab' : ''"
              @dblclick="openFromEvent(s, item, $event)"
              @auxclick="auxClick(s, item, $event)"
              @mousedown.middle.prevent
              @click="ctrlClick(s, item, $event)"
            >
              <td
                v-for="c in colsFor(s)"
                :key="c.field"
                :data-field="c.field"
                :class="[c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : '', c.link ? 'rb-link-cell' : '']"
              >{{ fmt(item[c.field], c.format) }}</td>
            </tr>
          </template>
          <template v-if="showTotals(s)" v-slot:body.append>
            <tr class="font-weight-bold">
              <td v-for="c in colsFor(s)" :key="c.field" :class="c.align === 'right' ? 'text-right' : ''">
                {{ totalCell(s, c) }}
              </td>
            </tr>
          </template>
        </v-data-table>
        <v-card-text v-if="truncated(s.dataset)" class="caption warning--text">
          Showing the first {{ rowsOf(s.dataset).length }} rows (result was truncated). Narrow the filters for complete data.
        </v-card-text>
      </v-card>

      <!-- Chart -->
      <v-card v-else-if="s.type === 'chart'" outlined>
        <v-card-title v-if="s.title" class="text-subtitle-1">{{ s.title }}</v-card-title>
        <v-card-text>
          <rb-chart :ref="'chart_' + s.id" :section="s" :rows="rowsOf(s.dataset)" />
        </v-card-text>
      </v-card>

      <!-- Text -->
      <div v-else-if="s.type === 'text'" style="white-space: pre-line">
        <h3 v-if="s.title">{{ s.title }}</h3>
        {{ s.content }}
      </div>
    </v-col>
  </v-row>
</template>

<script>
import RbChart from './RbChart.vue'
import { formatValue, aggregate, columnsFor, totalsRow, exportTable } from './rbUtil'
import { openRecord, recordHref } from './rbRecordLinks'

export default {
  name: 'RbReportRenderer',
  components: { RbChart },
  props: {
    template: { type: Object, required: true },
    result: { type: Object, required: true },
    // id of a drill-down: render that data view's sections instead of the report's
    drill: { type: String, default: null },
    // returns the details block (filters, who ran it...) added to downloads; the builder preview leaves it out
    detailsCtx: { type: Function, default: null }
  },
  data () {
    return { search: {} }
  },
  computed: {
    sections () {
      // sections switched off in the layout are not drawn, so everything below them moves up
      const all = this.drill ? ((this.template.drilldowns || []).find((x) => x.id === this.drill) || { ui: { sections: [] } }).ui.sections : this.template.ui.sections
      return all.filter((s) => !s.hidden)
    }
  },
  methods: {
    fmt: formatValue,
    ds (name) { return this.result.datasets[name] || { columns: [], rows: [] } },
    // ---- table search / sort / download ----
    rowText (s, item) {
      return this.colsFor(s).map((c) => `${item[c.field] === null || item[c.field] === undefined ? '' : item[c.field]} ${formatValue(item[c.field], c.format)}`).join(' ').toLowerCase()
    },
    // rows matching the table's search box (every shown column, as displayed or as raw); totals use these too
    shownRows (s) {
      const q = (this.search[s.id] || '').trim().toLowerCase()
      const rows = this.rowsOf(s.dataset)
      return q ? rows.filter((r) => this.rowText(s, r).includes(q)) : rows
    },
    defaultSortBy (s) { return s.sortable !== false && s.defaultSort && s.defaultSort.field ? [s.defaultSort.field] : [] },
    defaultSortDesc (s) { return s.sortable !== false && s.defaultSort && s.defaultSort.field ? [!!s.defaultSort.desc] : [] },
    async download (s, type) {
      const cols = this.allCols(s).map((c) => ({ field: c.field, label: c.label }))
      await exportTable({
        type, name: s.title || s.dataset, columns: cols, rows: this.shownRows(s),
        ctx: this.detailsCtx ? this.detailsCtx() : null
      })
    },
    rowsOf (name) { return this.ds(name).rows },
    truncated (name) { return !!this.ds(name).truncated },
    kpiValue (it) {
      return formatValue(aggregate(this.rowsOf(it.dataset), it.field, it.aggregate), it.format || 'number')
    },
    allCols (s) { return columnsFor(s.columns, this.ds(s.dataset)) },
    colsFor (s) { return this.allCols(s).filter((c) => !c.hidden) },
    headersFor (s) {
      return this.colsFor(s).map((c) => ({
        text: c.label, value: c.field, sortable: s.sortable !== false && c.sortable !== false, align: c.align === 'right' ? 'end' : c.align === 'center' ? 'center' : 'start'
      }))
    },
    showTotals (s) {
      return s.showTotals && this.colsFor(s).some((c) => c.total && c.total !== 'none')
    },
    totalCell (s, c) {
      const t = totalsRow(this.colsFor(s), this.shownRows(s))
      return c.total && c.total !== 'none' ? formatValue(t[c.field], c.format) : t[c.field]
    },
    // ---- record links: double-click / middle-click / ctrl-click open the record in a new tab ----
    hasLinks (s) { return this.allCols(s).some((c) => c.link) },
    linkOf (col, item) {
      if (!col || !col.link) return null
      const id = item[col.linkField || col.field]
      return recordHref(this.$router, col.link, id) ? { type: col.link, id } : null
    },
    rowLink (s, item) {
      for (const c of this.colsFor(s)) {
        const l = this.linkOf(c, item)
        if (l) return l
      }
      return null
    },
    // the clicked cell's own link if it has one, otherwise the row's first link
    linkFromEvent (s, item, e) {
      const td = e.target && e.target.closest ? e.target.closest('td[data-field]') : null
      const col = td ? this.colsFor(s).find((c) => c.field === td.dataset.field) : null
      return this.linkOf(col, item) || this.rowLink(s, item)
    },
    openFromEvent (s, item, e) {
      const l = this.linkFromEvent(s, item, e)
      if (l) openRecord(this.$router, l.type, l.id)
    },
    auxClick (s, item, e) {
      if (e.button !== 1) return
      e.preventDefault()
      this.openFromEvent(s, item, e)
    },
    ctrlClick (s, item, e) {
      if (e.ctrlKey || e.metaKey) this.openFromEvent(s, item, e)
    },
    // chart images keyed by section id, for printing
    chartImages () {
      const out = {}
      for (const s of this.sections) {
        if (s.type !== 'chart') continue
        const ref = this.$refs['chart_' + s.id]
        const comp = Array.isArray(ref) ? ref[0] : ref
        if (comp && comp.getImage) out[s.id] = comp.getImage()
      }
      return out
    }
  }
}
</script>

<style scoped>
.rb-row-linked { cursor: pointer; }
.rb-row-linked:hover { background: rgba(25, 118, 210, 0.06); }
.rb-link-cell { text-decoration: underline dotted; text-underline-offset: 3px; }
</style>
